/**
 * Solar position after the NOAA / Astronomical Almanac low-precision formulas (error < 0.5°),
 * enough to follow the light of the day. No network, no dependencies.
 */
const RAD = Math.PI / 180;
const J2000_MS = Date.UTC(2000, 0, 1, 12);

export interface SunPosition {
  /** Degrees above the horizon (negative = below). */
  elevation: number;
  /** Solar declination in degrees. */
  declination: number;
}

export function sunPosition(date: Date, latitude: number, longitude: number): SunPosition {
  const d = (date.getTime() - J2000_MS) / 86_400_000;
  const g = (357.529 + 0.98560028 * d) * RAD; // mean anomaly
  const q = 280.459 + 0.98564736 * d; // mean longitude
  const lambda = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD; // ecliptic longitude
  const epsilon = (23.439 - 0.00000036 * d) * RAD; // obliquity

  const rightAscension = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda));
  const declination = Math.asin(Math.sin(epsilon) * Math.sin(lambda));
  const gmstHours = (((18.697374558 + 24.06570982441908 * d) % 24) + 24) % 24;
  const hourAngle = (gmstHours * 15 + longitude) * RAD - rightAscension;

  const lat = latitude * RAD;
  const elevation = Math.asin(
    Math.sin(lat) * Math.sin(declination) +
      Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngle),
  );
  return { elevation: elevation / RAD, declination: declination / RAD };
}

/** Highest and lowest sun elevation of the day for this declination (solar noon / midnight). */
export function dailyExtremes(latitude: number, declination: number) {
  return {
    max: 90 - Math.abs(latitude - declination),
    min: Math.abs(latitude + declination) - 90,
  };
}
